// =============================================================================
// 2120, Phase 45: Private Beta UI primitives
// =============================================================================
// Single file housing four small components that work together:
//
//   1. BetaTosModal      , one-time disclaimer on first login as a beta user
//   2. BetaWelcomeBanner , calm gold card on first dashboard visit
//   3. BetaFeedbackFab   , floating action button + drawer (always available
//                            to authenticated users; not just beta testers,
//                            so future paying customers can use it too)
//   4. BetaIndicator     , small "Beta · Horizon" pill for header / sidebar
//
// All four are mounted at the AppShell layer so they're page-agnostic.
// Each component is defensive against `profile` being null during initial
// hydration (we never want a flash of "Welcome to Free" or similar).
// =============================================================================
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { isBetaActive, effectivePlan, planMeta } from '@/lib/entitlements';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sparkles, MessageCircleHeart, Send, ShieldCheck, X, Loader2, Beaker } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PLAN_LABEL = { clarity: 'Clarity', horizon: 'Horizon', summit: 'Summit' };

// ----- 1. ToS / disclaimer modal -------------------------------------------
function BetaTosModal({ session, betaInfo, refetch }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Only show when we KNOW the user is a beta tester AND has not seen
  // the ToS yet. Wait until billingMe has resolved (betaInfo present) so
  // we never flash the modal on a non-beta user.
  useEffect(() => {
    if (!betaInfo) return;
    if (!betaInfo.is_beta) return;
    if (betaInfo.tos_seen_at) return;
    setOpen(true);
  }, [betaInfo]);

  const acknowledge = async () => {
    setBusy(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/billing/beta/tos-seen`,
        {},
        { headers: { Authorization: `Bearer ${session?.access_token}` } }
      );
      setOpen(false);
      refetch && refetch();
    } catch (e) {
      // Best-effort, close the modal locally so we don't trap the user.
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && betaInfo?.tos_seen_at) setOpen(false); }}>
      <DialogContent
        className="rounded-2xl max-w-lg"
        data-testid="beta-tos-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <Beaker className="h-4 w-4 text-[hsl(var(--gold))]" />
            <Badge variant="outline" className="rounded-full border-[hsl(var(--gold))]/40 text-[hsl(var(--gold))] text-[10px]">
              Private beta
            </Badge>
          </div>
          <DialogTitle className="font-display tracking-tight">
            Welcome aboard.
          </DialogTitle>
          <DialogDescription className="text-sm leading-relaxed text-muted-foreground">
            You’re one of the first people inside 2120. A few quiet ground rules so we’re aligned:
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm leading-relaxed">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-[hsl(var(--gold))] shrink-0" />
            <span><strong className="text-foreground">This is pre-release software.</strong> Things will move and occasionally break. We test heavily, but you’ll find rough edges.</span>
          </div>
          <div className="flex items-start gap-3">
            <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-[hsl(var(--gold))] shrink-0" />
            <span><strong className="text-foreground">Your data is treated like production data.</strong> If we ever need to reset, we’ll warn you 14 days ahead.</span>
          </div>
          <div className="flex items-start gap-3">
            <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-[hsl(var(--gold))] shrink-0" />
            <span><strong className="text-foreground">No card, no payment</strong> for the duration of beta. You have full <strong className="text-foreground">{PLAN_LABEL[betaInfo?.beta_plan] || 'Horizon'}</strong> access.</span>
          </div>
          <div className="flex items-start gap-3">
            <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-[hsl(var(--gold))] shrink-0" />
            <span><strong className="text-foreground">Tell us what you think.</strong> The “Send feedback” button at the bottom right reaches us directly. Every note shapes what we ship next.</span>
          </div>
        </div>
        <DialogFooter>
          <Button
            className="rounded-xl w-full"
            onClick={acknowledge}
            disabled={busy}
            data-testid="beta-tos-acknowledge"
          >
            {busy ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> One moment</> : 'I understand, let me in'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ----- 2. Welcome banner ----------------------------------------------------
// Shown on the Dashboard page only (mounted within Dashboard.js if desired).
// Exported but not mounted at AppShell level, too noisy if it appears on
// every page. Left as a building block for now.
export function BetaWelcomeBanner({ betaInfo }) {
  if (!betaInfo?.is_beta) return null;
  const label = PLAN_LABEL[betaInfo.beta_plan] || 'Horizon';
  return (
    <Card data-testid="beta-welcome-banner" className="rounded-2xl border-[hsl(var(--gold))]/40 bg-gradient-to-br from-[hsl(var(--gold))]/10 via-[hsl(var(--gold))]/5 to-transparent">
      <CardContent className="p-4 flex items-start gap-3">
        <div className="h-9 w-9 rounded-xl bg-[hsl(var(--gold))]/15 grid place-items-center shrink-0 ring-1 ring-[hsl(var(--gold))]/30">
          <Sparkles className="h-4 w-4 text-[hsl(var(--gold))]" />
        </div>
        <div className="text-sm leading-relaxed">
          <div className="font-medium">You’re inside the 2120 private beta &middot; {label} access.</div>
          <p className="text-muted-foreground text-xs mt-1">No card, no payment. Try everything. Tell us what’s missing using the feedback button at the bottom right.</p>
        </div>
      </CardContent>
    </Card>
  );
}

// ----- 3. Floating feedback FAB + drawer ------------------------------------
function BetaFeedbackFab({ session, profile, betaInfo }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const submit = async () => {
    const text = (message || '').trim();
    if (!text) {
      toast.error('Write a short note so we know what to look at.');
      return;
    }
    setSending(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/beta/feedback`,
        {
          message: text,
          page_url: typeof window !== 'undefined' ? window.location.href : null,
          funnel_stage: deriveFunnelStage(profile),
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        },
        { headers: { Authorization: `Bearer ${session?.access_token}` } }
      );
      toast.success('Thanks, we got it.', {
        description: 'A human will read this. We may reply directly if helpful.',
        duration: 5000,
      });
      setMessage('');
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send right now. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // Only render the FAB for authenticated sessions.
  if (!session) return null;

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        size="sm"
        className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-40 rounded-full shadow-lg h-11 pl-4 pr-5 gap-2 bg-[hsl(var(--gold))]/95 hover:bg-[hsl(var(--gold))] text-[#1f2316]"
        data-testid="beta-feedback-fab"
        aria-label="Send feedback"
      >
        <MessageCircleHeart className="h-4 w-4" />
        <span className="text-xs font-medium">Send feedback</span>
      </Button>
      <Sheet open={open} onOpenChange={(v) => { if (!sending) setOpen(v); }}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-md"
          data-testid="beta-feedback-drawer"
        >
          <SheetHeader>
            <SheetTitle className="font-display tracking-tight">Tell us what you think</SheetTitle>
            <SheetDescription className="text-xs leading-relaxed">
              A real human will read this within one business day. We attach your page, plan, and account context automatically so you don’t have to repeat yourself.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-3">
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What’s working? What feels off? What’s missing?, anything goes."
              rows={9}
              className="rounded-xl resize-none"
              data-testid="beta-feedback-message"
              disabled={sending}
            />
            <div className="text-[10px] text-muted-foreground space-y-0.5">
              <div className="flex items-center gap-1.5"><ShieldCheck className="h-3 w-3" /> Sent to: support@2120.money</div>
              <div className="pl-4">Including: this page URL + your plan ({betaInfo?.beta_plan ? `${PLAN_LABEL[betaInfo.beta_plan]} (beta)` : (profile?.plan || 'free')})</div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                variant="ghost"
                onClick={() => setOpen(false)}
                disabled={sending}
                className="rounded-xl"
              >Cancel</Button>
              <Button
                onClick={submit}
                disabled={sending || !message.trim()}
                className="rounded-xl flex-1"
                data-testid="beta-feedback-submit"
              >
                {sending ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> Sending</> : <><Send className="h-3.5 w-3.5 mr-1.5" /> Send</>}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

// Cheap, best-effort funnel hint so support can see roughly where in the
// onboarding journey the user is when they send feedback. The real funnel
// telemetry lives in /sadmin#funnel, this is just a debug hint.
function deriveFunnelStage(profile) {
  if (!profile) return 'unknown';
  if (profile.onboarding_step && profile.onboarding_step !== 'done') {
    return `onboarding:${profile.onboarding_step}`;
  }
  if (profile.first_seen_at && !profile.first_transaction_at) return 'no_transactions_yet';
  if (profile.first_pdf_imported_at) return 'imported_pdf';
  if (profile.first_reflection_at) return 'reflected';
  return 'active';
}

// ----- 4. Mount everything in a single wrapper ------------------------------
// Drop <BetaUiHost /> into AppShell once and it handles fetching billing/me,
// rendering the ToS modal, and showing the floating feedback button. The
// feedback button shows for ALL authed users (we want feedback from free
// users too); only the ToS modal is gated to beta.
export default function BetaUiHost() {
  const { session, profile } = useAuth();
  const [betaInfo, setBetaInfo] = useState(null);

  const fetchBilling = useMemo(() => {
    return async () => {
      if (!session?.access_token) return;
      try {
        const r = await axios.get(`${BACKEND_URL}/api/billing/me`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        setBetaInfo(r.data?.beta || null);
      } catch {
        setBetaInfo(null);
      }
    };
  }, [session?.access_token]);

  useEffect(() => {
    fetchBilling();
  }, [fetchBilling]);

  return (
    <>
      <BetaTosModal session={session} betaInfo={betaInfo} refetch={fetchBilling} />
      <BetaFeedbackFab session={session} profile={profile} betaInfo={betaInfo} />
    </>
  );
}

export { BetaTosModal, BetaFeedbackFab };
